// src/components/Withdrawal.tsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Wallet,
  Send,
  RefreshCw,
  Clock,
  ChevronRight,
  Shield,
  Smartphone,
  Banknote,
  ArrowRight,
  Settings,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { withdrawalService, type BalanceSummary, type UIWithdrawalStatus } from "../services/withdrawalService";
import { useWithdrawalEligibility } from "../hooks/useWithdrawalEligibility";
import { useLocalization } from '../contexts/LocalizationContext';
import LoadingScreen from './LoadingScreen';

type Operator = "mpesa" | "airtel" | "orange" | "afrimoney";
type Currency = "CDF" | "USD";

interface WithdrawalHistoryItem {
  id: string;
  amount: number;
  currency: string;
  method: string;
  customer_number: string;
  reference: string;
  status: UIWithdrawalStatus;
  created_at: string;
  note?: string;
}

function formatCurrency(amount: number, currency: Currency): string {
  if (currency === "CDF") {
    return new Intl.NumberFormat('fr-CD', {
      style: 'currency', currency: 'CDF',
      minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(amount).replace('CDF', 'FC');
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 2, maximumFractionDigits: 2
  }).format(amount);
}

function formatDate(dateString: string): string {
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    }).format(new Date(dateString));
  } catch { return dateString; }
}

function mapStatus(raw?: string): UIWithdrawalStatus {
  const s = (raw || "").toLowerCase();
  if (["success", "successful", "succeeded", "completed", "paid"].includes(s)) return "successful";
  if (["fail", "failed", "error", "rejected", "canceled", "cancelled"].includes(s)) return "failed";
  if (["processing", "in_progress", "pending_processing"].includes(s)) return "processing";
  return "pending";
}

const operatorLogos: Record<Operator, string> = {
  mpesa: "/mpesa.png",
  airtel: "/airtel.png",
  orange: "/orange.png",
  afrimoney: "/afrimoney.png",
};

const operatorLabels: Record<Operator, string> = {
  mpesa: "M-Pesa",
  airtel: "Airtel Money",
  orange: "Orange Money",
  afrimoney: "Afrimoney",
};

const Withdrawal: React.FC = () => {
  const { t } = useLocalization();
  const { user } = useAuth();
  const { data: eligibility, isLoading: isLoadingEligibility } = useWithdrawalEligibility();

  const [balanceSummary, setBalanceSummary] = useState<Record<string, BalanceSummary>>({});
  const [withdrawalHistory, setWithdrawalHistory] = useState<WithdrawalHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingBalances, setLoadingBalances] = useState(false);

  // Withdrawal config — read-only view. Editing happens in /cashout-settings.
  const [wConfig, setWConfig] = useState<{ email: string; phone_airtel: string; phone_mpesa: string; phone_orange: string; phone_afrimoney: string } | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [pendingRequest, setPendingRequest] = useState<{
    id: number;
    requested_at: string;
    requested_values: Record<string, string | null>;
    diff_summary: Array<{ field: string; from: any; to: any }>;
  } | null>(null);
  const [lastReview, setLastReview] = useState<{
    status: 'approved' | 'rejected';
    reviewed_at: string | null;
    rejection_reason?: string | null;
  } | null>(null);

  const [operator, setOperator] = useState<Operator>("mpesa");
  const [currency, setCurrency] = useState<Currency>("CDF");
  const [amount, setAmount] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"withdraw" | "history">("withdraw");

  // OTP flow
  const [flowStep, setFlowStep] = useState<"form" | "otp" | "done">("form");
  const [otpReference, setOtpReference] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpMessage, setOtpMessage] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [resultMessage, setResultMessage] = useState("");
  const [resultStatus, setResultStatus] = useState("");
  const [resultReference, setResultReference] = useState("");

  const isAllowed = eligibility?.has_withdrawal_access || false;

  // Load operator balances
  const loadBalances = useCallback(async () => {
    setLoadingBalances(true);
    try {
      const data = await withdrawalService.getBalanceSummary();
      setBalanceSummary(data);
    } catch { /* */ }
    finally { setLoadingBalances(false); }
  }, []);

  // Load history
  const loadWithdrawalHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const result = await withdrawalService.getHistory(1, 20);
      const tx: WithdrawalHistoryItem[] = (result.transactions || []).map((t: any) => ({
        id: t.reference || t.id,
        amount: t.amount,
        currency: t.currency,
        method: t.method,
        customer_number: t.customer_number || "",
        reference: t.reference,
        status: mapStatus(t.status),
        created_at: t.created_at,
      }));
      setWithdrawalHistory(tx);
    } catch { setWithdrawalHistory([]); }
    finally { setLoadingHistory(false); }
  }, []);

  const loadConfig = useCallback(async () => {
    setConfigLoading(true);
    try {
      const data = await withdrawalService.getWithdrawalConfig();
      if (data && data.configured) {
        setWConfig(data);
      } else {
        setWConfig(null);
      }
      setPendingRequest(data?.pending_request || null);
      setLastReview(data?.last_review || null);
    } catch {
      setWConfig(null);
      setPendingRequest(null);
      setLastReview(null);
    } finally { setConfigLoading(false); }
  }, []);

  useEffect(() => {
    if (isAllowed && user) {
      loadBalances();
      loadWithdrawalHistory();
      loadConfig();
    }
  }, [isAllowed, user, loadBalances, loadWithdrawalHistory, loadConfig]);

  // Resend OTP timer
  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => setResendTimer(t => t - 1), 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Computed
  const amountNumber = useMemo(() => {
    const v = Number(String(amount).replace(",", "."));
    return Number.isFinite(v) ? Math.round(v * 100) / 100 : 0;
  }, [amount]);

  const totalBalance = useMemo(() => {
    const summary = balanceSummary[currency];
    if (!summary) return 0;
    if (summary.operator_details?.length) {
      return summary.operator_details.reduce((sum, d) => {
        const dep = parseFloat(String(d.realtime_deposit ?? 0)) || 0;
        const wth = parseFloat(String(d.realtime_withdrawal ?? 0)) || 0;
        return sum + dep + wth;
      }, 0);
    }
    return parseFloat(String(summary.total_balance ?? summary.available_for_withdrawal ?? 0)) || 0;
  }, [currency, balanceSummary]);

  const configuredPhone = useMemo(() => {
    if (!wConfig) return null;
    const map: Record<string, string> = { mpesa: wConfig.phone_mpesa, airtel: wConfig.phone_airtel, orange: wConfig.phone_orange, afrimoney: wConfig.phone_afrimoney };
    return map[operator] || null;
  }, [wConfig, operator]);

  const canSubmit = amountNumber > 0 && amountNumber <= totalBalance && !!operator && !!currency && !!configuredPhone;

  // Reset form
  const resetForm = useCallback(() => {
    setAmount("");
    setPhone("");
    setError(null);
    setFlowStep("form");
    setOtpReference("");
    setOtpCode("");
    setOtpMessage("");
    setResultMessage("");
    setResultStatus("");
    setResultReference("");
  }, []);

  // Step 1: Initiate withdrawal (sends OTP)
  const handleInitiate = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const response = await withdrawalService.initiateOperatorWithdrawal({
        amount: amountNumber,
        currency,
        method: operator,
        phone,
      });

      if (response.status === "otp_required") {
        setOtpReference(response.reference);
        setOtpMessage(response.message || "Un code OTP a ete envoye a votre email.");
        setFlowStep("otp");
        setResendTimer(180);
      } else if (["success", "pending"].includes(response.status)) {
        setResultMessage(response.message || "Retrait initie avec succes.");
        setResultStatus(response.status);
        setResultReference(response.reference || "");
        setFlowStep("done");
        loadBalances();
        loadWithdrawalHistory();
      } else if (response.status === "pending_approval") {
        setResultMessage(response.message || "Votre retrait depasse la limite et a ete soumis pour approbation.");
        setResultStatus("pending_approval");
        setResultReference(response.reference || "");
        setFlowStep("done");
      }
    } catch (e: any) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.error_type === "limit_exceeded") {
        const d = detail.details || {};
        setError(`Limite hebdomadaire depassee. Limite: ${d.weekly_limit || "N/A"} ${currency}, Deja retire: ${d.already_withdrawn || 0} ${currency}, Restant: ${d.remaining_limit || 0} ${currency}`);
      } else {
        setError(typeof detail === "string" ? detail : (detail?.message || e.message || "Echec du retrait"));
      }
    } finally { setSubmitting(false); }
  };

  // Step 2: Confirm with OTP
  const handleConfirmOTP = async () => {
    if (!otpCode || otpCode.length !== 6) {
      setError("Veuillez entrer le code OTP a 6 chiffres");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await withdrawalService.initiateOperatorWithdrawal({
        amount: amountNumber,
        currency,
        method: operator,
        phone,
        otp_code: otpCode,
        reference: otpReference,
        otp_reference: otpReference,
      });

      if (["success", "pending"].includes(response.status)) {
        setResultMessage(response.message || "Retrait initie avec succes.");
        setResultStatus(response.status);
        setResultReference(response.reference || otpReference);
        setFlowStep("done");
        loadBalances();
        loadWithdrawalHistory();
      } else if (response.status === "pending_approval") {
        setResultMessage(response.message || "Votre retrait a ete soumis pour approbation.");
        setResultStatus("pending_approval");
        setResultReference(response.reference || otpReference);
        setFlowStep("done");
      } else {
        setError(response.message || "Echec de la verification OTP");
      }
    } catch (e: any) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (e.message || "Echec du retrait"));
    } finally { setSubmitting(false); }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    try {
      await withdrawalService.resendOTP(otpReference);
      setResendTimer(180);
      setOtpMessage("Un nouveau code OTP a ete envoye a votre email.");
    } catch { setError("Echec du renvoi de l'OTP"); }
  };

  const getStatusBadge = (status: UIWithdrawalStatus) => {
    const config = {
      successful: { bg: "bg-emerald-100", text: "text-emerald-800", label: "Succes" },
      failed: { bg: "bg-rose-100", text: "text-rose-800", label: "Echoue" },
      processing: { bg: "bg-blue-100", text: "text-blue-800", label: "En cours" },
      pending: { bg: "bg-amber-100", text: "text-amber-800", label: "En attente" },
    }[status];
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (isLoadingEligibility) {
    return <LoadingScreen fullscreen={false} message={t('withdrawal.verifying')} />;
  }

  if (!isAllowed) {
    return (
      <div className="max-w-lg mx-auto mt-12">
        <div className="bg-gradient-to-br from-white to-slate-50 rounded-2xl border border-slate-200 p-8 shadow-sm">
          <div className="flex flex-col items-center text-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-rose-100 to-pink-100 flex items-center justify-center mb-6">
              <Shield className="h-10 w-10 text-rose-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-3">{t('withdrawal.accessRequired')}</h2>
            <p className="text-slate-600 mb-6 text-sm leading-relaxed">
              {t('withdrawal.accessRequiredDesc')}
            </p>
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 w-full">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">{t('withdrawal.merchantAccount')}</p>
                  <p className="text-base font-semibold text-slate-900">{user?.merchant_code || 'N/A'}</p>
                </div>
                <div className="px-3 py-1 bg-rose-100 rounded-full">
                  <span className="text-xs font-medium text-rose-700">{t('withdrawal.limitedAccess')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-5 sm:p-6 text-white">
        <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)", backgroundSize: "28px 28px" }} />
        <div className="absolute -top-32 -right-20 w-96 h-96 bg-emerald-500/20 rounded-full blur-[100px]" />
        <div className="absolute -bottom-32 -left-20 w-96 h-96 bg-teal-500/15 rounded-full blur-[100px]" />
        <div className="relative flex items-center justify-between">
          <div>
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1 mb-3">
              <Banknote className="w-3 h-3 text-emerald-400" />
              <span className="text-[11px] font-semibold text-emerald-300 tracking-wider uppercase">{t('withdrawal.title')}</span>
            </div>
            <h1 className="text-lg sm:text-xl font-bold tracking-tight">{t('withdrawal.title')}</h1>
            <p className="text-sm text-slate-400 mt-1">{t('withdrawal.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2 bg-white/[0.06] backdrop-blur-sm border border-white/10 rounded-xl px-3.5 py-2">
            <span className="text-xs text-slate-400">{t('withdrawal.merchant')}</span>
            <span className="text-sm font-semibold text-white">{user?.merchant_code}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        <button onClick={() => setActiveTab("withdraw")}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === "withdraw" ? "border-emerald-500 text-emerald-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          <div className="flex items-center"><Banknote className="h-4 w-4 mr-2" />{t('withdrawal.new')}</div>
        </button>
        <button onClick={() => { setActiveTab("history"); loadWithdrawalHistory(); }}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === "history" ? "border-emerald-500 text-emerald-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          <div className="flex items-center"><Clock className="h-4 w-4 mr-2" />{t('withdrawal.history')}</div>
        </button>
      </div>

      {activeTab === "withdraw" ? (
        <div className="max-w-4xl mx-auto w-full space-y-6">
          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-rose-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-rose-800"><span className="font-semibold">{t('common.error')}:</span> {error}</div>
            </div>
          )}

          {/* Form */}
          {flowStep === "form" && (
            <>
              {/* Source / Target balance cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4 border border-blue-200 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700">{t('withdrawal.sourceTitle')}</h3>
                      <p className="text-xs text-gray-500">{t('withdrawal.sourceSubtitle')} · {currency}</p>
                    </div>
                    <div className="w-9 h-9 rounded-lg bg-emerald-600 flex items-center justify-center text-white">
                      <Wallet className="h-4 w-4" />
                    </div>
                  </div>
                  {loadingBalances ? (
                    <div className="h-10 flex items-center"><div className="w-32 h-4 bg-gray-200 rounded animate-pulse" /></div>
                  ) : (
                    <p className="text-2xl font-bold text-gray-900 tabular-nums">
                      {formatCurrency(totalBalance, currency)}
                    </p>
                  )}
                </div>

                <div className="bg-white rounded-lg p-4 border border-emerald-200 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700">{t('withdrawal.targetTitle')}</h3>
                      <p className="text-xs text-gray-500">{operatorLabels[operator]}</p>
                    </div>
                    <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center text-white overflow-hidden">
                      <img src={operatorLogos[operator]} alt={operator} className="w-6 h-6 object-contain" />
                    </div>
                  </div>
                  {configLoading ? (
                    <div className="h-10 flex items-center"><div className="w-32 h-4 bg-gray-200 rounded animate-pulse" /></div>
                  ) : configuredPhone ? (
                    <p className="text-base font-semibold text-emerald-700">{configuredPhone}</p>
                  ) : (
                    <p className="text-sm text-amber-600 font-medium">{t('withdrawal.phoneNotConfigured')}</p>
                  )}
                </div>
              </div>

              {/* Configuration */}
              <div className="bg-white rounded-lg p-5 border border-gray-200">
                <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                  <Wallet className="h-4 w-4 mr-2 text-emerald-600" />
                  {t('withdrawal.configTitle')}
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">{t('withdrawal.paymentMethod')}</label>
                    <div className="grid grid-cols-4 gap-3">
                      {(["mpesa", "airtel", "orange", "afrimoney"] as Operator[]).map((op) => (
                        <button key={op} type="button" onClick={() => setOperator(op)}
                          className={`p-3 rounded-xl border-2 transition-all ${operator === op ? "border-emerald-500 bg-emerald-50 shadow-sm" : "border-slate-200 hover:border-slate-300"}`}>
                          <div className="flex flex-col items-center gap-2">
                            <img src={operatorLogos[op]} alt={op} className="w-9 h-9 object-contain" />
                            <span className="text-xs font-medium text-slate-700">{operatorLabels[op]}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">{t('common.currency')}</label>
                    <div className="grid grid-cols-2 gap-3">
                      {(["CDF", "USD"] as Currency[]).map((curr) => (
                        <button key={curr} type="button" onClick={() => setCurrency(curr)}
                          className={`px-3 py-2.5 rounded-xl border-2 text-sm font-medium transition-all ${currency === curr ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200 hover:border-slate-300 text-slate-700"}`}>
                          {curr}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Details */}
              <div className="bg-white rounded-lg p-5 border border-gray-200">
                <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                  <Send className="h-4 w-4 mr-2 text-emerald-600" />
                  {t('withdrawal.detailsTitle')}
                </h3>

                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-slate-700">{t('common.amount')}</label>
                      <button type="button" onClick={() => setAmount(totalBalance.toFixed(2))}
                        disabled={!totalBalance}
                        className="text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2 py-0.5 rounded disabled:opacity-50">
                        Max
                      </button>
                    </div>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 text-sm">{currency === "CDF" ? "FC" : "$"}</div>
                      <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                        placeholder="0.00" step="0.01" min="0" max={totalBalance}
                        className="w-full pl-12 pr-3 py-2.5 text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent tabular-nums" />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {t('withdrawal.amountAvailable')} : {formatCurrency(totalBalance, currency)}
                    </p>
                    {amountNumber > totalBalance && totalBalance > 0 && (
                      <p className="text-xs text-rose-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {t('withdrawal.insufficientBalance')}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Recipient phone (read-only summary; edits live in /cashout-settings) */}
              <div className="bg-white rounded-lg p-5 border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-semibold text-gray-900 flex items-center">
                    <Smartphone className="h-4 w-4 mr-2 text-emerald-600" />
                    {t('withdrawal.recipientCard')}
                  </h3>
                  <Link
                    to="/cashout-settings"
                    className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800"
                  >
                    <Settings className="h-3.5 w-3.5" />
                    {t('withdrawal.openCashoutSettings') || 'Paramètres de décaissement'}
                  </Link>
                </div>

                {pendingRequest && (
                  <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2">
                    <Clock className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-900">
                        {t('withdrawal.configPendingTitle') || "Demande de modification en attente"}
                      </p>
                      <p className="text-xs text-amber-800 mt-0.5">
                        {t('withdrawal.configPendingShort') || "Le backoffice doit approuver vos derniers changements avant qu'ils s'appliquent. Vous pouvez continuer à décaisser avec la configuration actuelle."}
                      </p>
                      <Link to="/cashout-settings" className="inline-block mt-1 text-xs font-medium text-amber-700 hover:underline">
                        {t('withdrawal.configReviewLink') || 'Voir les détails →'}
                      </Link>
                    </div>
                  </div>
                )}

                {lastReview?.status === 'rejected' && !pendingRequest && (
                  <div className="mb-3 p-3 rounded-lg bg-rose-50 border border-rose-200 flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-rose-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-rose-900">
                        {t('withdrawal.configRejectedTitle') || 'Dernière demande rejetée'}
                      </p>
                      {lastReview.rejection_reason && (
                        <p className="text-xs text-rose-800 mt-0.5">{lastReview.rejection_reason}</p>
                      )}
                      <Link to="/cashout-settings" className="inline-block mt-1 text-xs font-medium text-rose-700 hover:underline">
                        {t('withdrawal.configFixLink') || 'Corriger →'}
                      </Link>
                    </div>
                  </div>
                )}

                {configLoading ? (
                  <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 flex items-center gap-3">
                    <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                    <span className="text-sm text-slate-500">{t('withdrawal.loadingConfig')}</span>
                  </div>
                ) : !wConfig ? (
                  <div className="space-y-3">
                    <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">{t('withdrawal.configRequired')}</p>
                        <p className="text-xs text-amber-700 mt-0.5">{t('withdrawal.configRequiredDesc')}</p>
                      </div>
                    </div>
                    <Link
                      to="/cashout-settings"
                      className="block w-full text-center px-4 py-2.5 rounded-lg border-2 border-dashed border-emerald-300 text-emerald-700 text-sm font-medium hover:bg-emerald-50 transition-colors"
                    >
                      {t('withdrawal.configurePhones')}
                    </Link>
                  </div>
                ) : (
                  <div className={`p-3 rounded-lg border flex items-center justify-between ${configuredPhone ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                    <div className="flex items-center gap-3">
                      <Smartphone className={`h-4 w-4 flex-shrink-0 ${configuredPhone ? 'text-emerald-600' : 'text-amber-600'}`} />
                      {configuredPhone ? (
                        <p className="text-sm text-emerald-800">
                          {t('withdrawal.cashoutTo')} <span className="font-bold">{configuredPhone}</span> ({operatorLabels[operator]})
                        </p>
                      ) : (
                        <p className="text-sm text-amber-700">{t('withdrawal.noPhoneFor')} {operatorLabels[operator]}</p>
                      )}
                    </div>
                    <Link
                      to="/cashout-settings"
                      className={`text-xs font-medium hover:underline ${configuredPhone ? 'text-emerald-700' : 'text-amber-700'}`}
                    >
                      {configuredPhone ? t('withdrawal.modify') : t('withdrawal.configure')}
                    </Link>
                  </div>
                )}
              </div>

              {/* Summary */}
              {amountNumber > 0 && configuredPhone && (
                <div className="bg-emerald-50 rounded-lg p-5 border border-emerald-200">
                  <h3 className="text-base font-semibold text-emerald-900 mb-4">
                    {t('withdrawal.summaryTitle')}
                  </h3>
                  <div className="flex items-center justify-between mb-4 p-4 bg-white rounded-lg border border-emerald-100">
                    <div className="flex items-center text-center flex-1">
                      <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center mr-3 text-white">
                        <Wallet className="h-4 w-4" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-gray-900 text-sm">{t('withdrawal.myWallets')}</p>
                        <p className="text-xs text-gray-600">{t('withdrawal.summaryTotal')} {currency}</p>
                        <p className="text-xs text-red-600 font-medium">- {amountNumber.toLocaleString()} {currency}</p>
                      </div>
                    </div>
                    <ArrowRight className="h-5 w-5 text-emerald-600 mx-3 flex-shrink-0" />
                    <div className="flex items-center text-center flex-1">
                      <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center mr-3 text-white overflow-hidden">
                        <img src={operatorLogos[operator]} alt={operator} className="w-7 h-7 object-contain" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-gray-900 text-sm">{operatorLabels[operator]}</p>
                        <p className="text-xs text-gray-600">{configuredPhone}</p>
                        <p className="text-xs text-green-600 font-medium">+ {amountNumber.toLocaleString()} {currency}</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm bg-white rounded-lg p-4 border border-emerald-100">
                    <div className="flex justify-between"><span className="text-gray-600">{t('withdrawal.summaryAmount')}</span><span className="font-semibold text-gray-900">{formatCurrency(amountNumber, currency)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">{t('withdrawal.summaryOperator')}</span><span className="font-medium text-emerald-700">{operatorLabels[operator]}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">{t('withdrawal.summaryPhone')}</span><span className="font-medium text-slate-900">{configuredPhone}</span></div>
                    <div className="flex justify-between pt-2 border-t border-gray-100">
                      <span className="font-medium text-emerald-900">{t('withdrawal.summaryStatus')}</span>
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">{t('withdrawal.summaryReady')}</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* OTP Step */}
          {flowStep === "otp" && (
            <div className="bg-white rounded-lg p-5 border border-blue-200">
              <div className="flex items-start mb-4">
                <Shield className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-blue-800 mb-1">{t('withdrawal.otpTitle')}</h3>
                  <p className="text-blue-700 text-sm">{otpMessage}</p>
                  <p className="text-blue-500 text-xs mt-2">{t('withdrawal.otpFor')} : {formatCurrency(amountNumber, currency)} {t('withdrawal.otpVia')} {operatorLabels[operator]}</p>
                </div>
              </div>
              <div className="space-y-3">
                <label className="block text-sm font-medium text-slate-700">{t('withdrawal.enterOtp')}</label>
                <input type="text" value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000" maxLength={6} autoFocus
                  className="w-full px-4 py-3 text-center text-2xl tracking-[0.5em] font-mono border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors" />
              </div>
              <div className="mt-3 flex items-center justify-between">
                <button onClick={handleResendOTP} disabled={resendTimer > 0}
                  className={`text-sm ${resendTimer > 0 ? "text-slate-400" : "text-blue-600 hover:underline cursor-pointer"}`}>
                  {resendTimer > 0 ? `${t('withdrawal.resendIn')} ${Math.floor(resendTimer / 60)}:${String(resendTimer % 60).padStart(2, "0")}` : t('withdrawal.resendOtp')}
                </button>
              </div>
            </div>
          )}

          {/* Done Step */}
          {flowStep === "done" && (
            <div className="p-6 rounded-lg border text-center"
              style={{ background: resultStatus === "pending_approval" ? "linear-gradient(to right, #fffbeb, #fef3c7)" : "linear-gradient(to right, #ecfdf5, #ccfbf1)",
                       borderColor: resultStatus === "pending_approval" ? "#fbbf24" : "#6ee7b7" }}>
              <CheckCircle2 className={`h-12 w-12 mx-auto mb-3 ${resultStatus === "pending_approval" ? "text-amber-500" : "text-emerald-500"}`} />
              <h3 className={`text-lg font-semibold mb-1 ${resultStatus === "pending_approval" ? "text-amber-800" : "text-emerald-800"}`}>
                {resultStatus === "pending_approval" ? t('withdrawal.donePending') : t('withdrawal.doneInitiated')}
              </h3>
              <p className={`text-sm mb-3 ${resultStatus === "pending_approval" ? "text-amber-700" : "text-emerald-700"}`}>{resultMessage}</p>
              {resultReference && (
                <p className="text-sm text-slate-600">{t('withdrawal.referenceLabel')} <code className="font-mono font-semibold">{resultReference}</code></p>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="bg-white rounded-lg p-5 border border-gray-200">
            <div className="flex gap-3">
              <button onClick={resetForm}
                className="px-5 py-2.5 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors text-sm font-medium">
                {flowStep === "done" ? t('withdrawal.newCashout') : t('withdrawal.clearForm')}
              </button>
              {flowStep === "form" && (
                <button onClick={handleInitiate} disabled={!canSubmit || submitting}
                  className={`flex-1 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all ${!canSubmit || submitting ? 'bg-slate-300 text-slate-500 cursor-not-allowed' : 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:shadow-lg'}`}>
                  <div className="flex items-center justify-center">
                    {submitting ? (<><Loader2 className="h-4 w-4 animate-spin mr-2" />{t('withdrawal.sending')}</>) : (<><Banknote className="h-4 w-4 mr-2" />{amountNumber > 0 ? `${t('withdrawal.cashoutBtn')} ${formatCurrency(amountNumber, currency)}` : t('withdrawal.cashoutBtn')}</>)}
                  </div>
                </button>
              )}
              {flowStep === "otp" && (
                <button onClick={handleConfirmOTP} disabled={otpCode.length !== 6 || submitting}
                  className={`flex-1 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all ${otpCode.length !== 6 || submitting ? 'bg-slate-300 text-slate-500 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-lg'}`}>
                  <div className="flex items-center justify-center">
                    {submitting ? (<><Loader2 className="h-4 w-4 animate-spin mr-2" />{t('withdrawal.confirming')}</>) : (<><Shield className="h-4 w-4 mr-2" />{t('withdrawal.confirmCashout')}</>)}
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* History */
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-900">{t('withdrawal.transactionHistory')}</h3>
              <button onClick={loadWithdrawalHistory} disabled={loadingHistory}
                className="px-4 py-2 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors">
                {loadingHistory ? <Loader2 className="h-4 w-4 animate-spin" /> : <div className="flex items-center"><RefreshCw className="h-4 w-4 mr-2" />{t('common.refresh')}</div>}
              </button>
            </div>

            {loadingHistory ? (
              <LoadingScreen fullscreen={false} message={t('withdrawal.loadingHistory')} />
            ) : withdrawalHistory.length > 0 ? (
              <div className="space-y-3">
                {withdrawalHistory.slice(0, 20).map((item) => (
                  <div key={item.id} className="group p-4 rounded-xl border border-slate-100 hover:border-slate-200 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg ${item.status === 'successful' ? 'bg-emerald-100' : item.status === 'failed' ? 'bg-rose-100' : item.status === 'processing' ? 'bg-blue-100' : 'bg-amber-100'}`}>
                          {item.status === 'successful' ? <CheckCircle2 className="h-4 w-4" /> : item.status === 'failed' ? <AlertCircle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-slate-900">{formatCurrency(item.amount, item.currency as Currency)}</span>
                            {getStatusBadge(item.status)}
                          </div>
                          <p className="text-xs text-slate-500 mt-1">{formatDate(item.created_at)}</p>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-slate-600" />
                    </div>
                    <div className="flex items-center justify-between text-sm text-slate-600">
                      <span className="capitalize">{item.method}</span>
                      <code className="text-xs text-slate-500 font-mono">{item.reference}</code>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4"><Clock className="h-8 w-8 text-slate-400" /></div>
                <h4 className="text-lg font-medium text-slate-900 mb-2">{t('withdrawal.noWithdrawals')}</h4>
                <p className="text-slate-600 text-sm max-w-sm mx-auto">{t('withdrawal.startFirst')}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Withdrawal;
